"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="222472" className="flex gap-6">
        <div className="h-5 w-16 animate-pulse rounded-md bg-muted" />
        <div className="h-5 w-16 animate-pulse rounded-md bg-muted" />
        <div className="h-5 w-16 animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-default font-semibold text-foreground transition-colors hover:text-primary">
                {item.label}
                {}
                <span className="absolute top-1/2 left-0 h-[2px] w-full origin-left -translate-y-1/2 scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-6">
        <div className="h-8 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-8 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-8 w-full animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <div className="flex flex-col gap-6 p-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-2xl font-default font-bold text-foreground transition-colors hover:text-primary" data-index={i}>
          {item.label}
        </Link>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "border-b border-border bg-background/80 backdrop-blur-xl" : "bg-background"}`}>
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-4 md:px-8">
        {}
        <Link to="/HomePage#" className="flex shrink-0 items-center">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636043/img/pottyforsale_logo.png" alt="PottyForSale.com" className="h-8 w-auto object-contain" />
        </Link>

        {}
        <div className="hidden md:flex md:flex-1 md:justify-center">
          <MenuProvider menu_id="2">
            <DesktopNav />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          {}
          <Button asChild className="rounded-lg bg-primary px-6 py-2 font-default font-bold text-primary-foreground shadow-lg transition-transform hover:scale-105 hover:bg-primary/90">
            <Link to="#quote">
              <Phone className="mr-2 size-4 animate-pulse" />
              <span className="hidden sm:inline">Get a Quote</span>
              <span className="sm:hidden">Quote</span>
            </Link>
          </Button>

          {}
          <div className="md:hidden">
            <SafeHydrate><Drawer direction="right">
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent" aria-label="Menü öffnen">
                  <Menu className="size-6" />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="h-full w-4/5 sm:max-w-sm">
                <DrawerHeader className="border-b border-border text-left">
                  <DrawerTitle className="sr-only">Navigation</DrawerTitle>
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636043/img/pottyforsale_logo.png" alt="PottyForSale.com" className="h-7 w-auto object-contain" />
                </DrawerHeader>
                <div className="flex-1 overflow-y-auto">
                  <MenuProvider menu_id="2">
                    <MobileNav />
                  </MenuProvider>
                </div>
              </DrawerContent>
            </Drawer></SafeHydrate>
          </div>
        </div>
      </div>
    </section>;
}